import { useState, useEffect } from "react";
import { get, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { addMonths, format } from "date-fns";
import { Box, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSubPrograms } from "../../hooks/useSubPrograms";
import { venueOptions } from "../../utils/programUtils";
import { currencyOptions, getCurrencySymbol } from "../../utils/programUtils";
import DeleteSubProgramDialog from "../../components/components/DeleteSubProgramDialog";
import ProgramLayout from "../../components/components/ProgramLayout";
import ProgramDetailsForm from "../../components/components/ProgramDetailsForm";
import BreadcrumbNavigation from "../../components/components/BreadcrumbNavigation";
import SubProgramsSection from "../../components/components/SubProgramsSection";
import mahatriaBg from "../../assets/images/Mahatria-sitting-image.png";
import styles from "./AddProgramPage.module.scss";
import { Button } from "../../common/components/Button";
import { getCall, postCall, putCall } from "../../services/apiService";
import { endPoints, PORTAL } from "../../constants/urlConstants";
import Loader from "../../common/components/Loader";
import { getCombinedLaunchDateTime } from "../../utils/dateTimeCombine";
import {
  formatTimeToHHMMSS,
  getProgramImage,
  getProgramImageForBanner,
  handleAWSFileUpload,
  handleQuestionList,
  parseTimeStringToDate,
} from "../../utils/commonFunctions";
import { getItemInLocalStorage } from "../../services/localStorage";
import Stepper from "../../components/programStepper";
import ArrowLeft from "../../assets/images/arrow-left.svg";
import RectangularProgramCard from "../../components/RectangularCard";
import RectangularCard from "../../components/RectangularCard";
import CustomVenueModal from "../../common/components/CustomVenueModal";
import InvoiceDetails from "../../components/components/InvoiceComponent";
import { validateFileTypeAndSize } from "../../utils/fileUploadValidation";

// Update the validation schema to be dynamic based on program type
const createProgramSchema = (programType: ProgramType) => {
  const baseSchema = {
    programName: yup.string().required("Program name is required"),
    description: yup.string(),
    banner: yup.string().nullable(),
    modeOfProgram: programType?.modeOfOperation
      ? yup.string().oneOf([programType?.modeOfOperation]).required()
      : yup.string().oneOf(["online", "offline", "hybrid"]).required(),
    startDate: yup
      .date()
      .nullable()
      .typeError("Start date is required")
      .required("Start date is required"),

    endDate: yup
      .date()
      .nullable()
      .typeError("End date is required")
      .min(yup.ref("startDate"), "End date must be after or same as start date")
      .required("End date is required"),
    hdbFee: yup.string().when("programName", {
      is: (name) => name?.includes("HDB"),
      then: () => yup.string().required("HDB Fee is required"),
      otherwise: () => yup.string(),
    }),
    msdFee: yup.string().when("programName", {
      is: (name) => name?.includes("MSD"),
      then: () => yup.string().required("MSD Fee is required"),
      otherwise: () => yup.string(),
    }),
    registrationStartDate: yup
      .date()
      .required("Registration start date is required")
      .max(
        yup.ref("startDate"),
        "Registration start date must be before program start date",
      ),
    registrationEndDate: yup
      .date()
      .min(
        yup.ref("registrationStartDate"),
        "Registration end date must be after registration start date",
      )
      .required("Registration end date is required"),
    tdsLimit: yup
      .number()
      .typeError("TDS limit must be a number")
      .min(0, "TDS limit cannot be negative")
      .max(100, "TDS limit cannot exceed 100%")
      .required("TDS limit is required"),

    cgstLimit: yup
      .number()
      .typeError("CGST limit must be a number")
      .min(0, "CGST limit cannot be negative")
      .max(100, "CGST limit cannot exceed 100%")
      .required("CGST limit is required"),
    sgstLimit: yup
      .number()
      .typeError("SGST limit must be a number")
      .min(0, "SGST limit cannot be negative")
      .max(100, "SGST limit cannot exceed 100%")
      .required("SGST limit is required"),

    igstLimit: yup
      .number()
      .typeError("IGST limit must be a number")
      .min(0, "IGST limit cannot be negative")
      .max(100, "IGST limit cannot exceed 100%")
      .required("IGST limit is required"),

    tdsApplicableTo: yup
      .string()

      .required("TDS applicable selection is required"),

    nameInInvoice: yup.string().trim().required("Name in invoice is required"),
    address: yup.string().required("Address is required"),

    pan: yup
      .string()
      .matches(
        /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        "Invalid PAN format (e.g. ABCDE1234F)",
      )
      .required("PAN is required")
      .length(10, "PAN must be exactly 10 characters."),

    gstin: yup
      .string()
      .length(15, "GSTIN must be exactly 15 characters")
      .matches(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
        "Invalid GSTIN format. Example: 27ABCDE1234F1Z5",
      )
      .required("GSTIN is required"),

    cin: yup
      .string()
      .length(21, "CIN must be exactly 21 characters.")
      .matches(
        /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
        "Invalid CIN format. Example: L12345MH2000PLC123456",
      )
      .length(21, "CIN must be exactly 21 characters")
      .required("CIN is required"),
    helpLineNumber: yup.string().required("Help line number is required"),
    emailSenderName: yup
      .string()
      .required("Email sender name is required")
      .min(3, "Email sender name must be 3 characters"),
    venueNameInEmail: yup
      .string()
      .required("Venue name in email is required")
      .min(3, "Venue name in email must be 3 characters"),
    launchStartDate: yup
      .date()
      .required("Launch start date is required")
      .max(yup.ref("startDate"), "Launch program before start the program"),
    launchStartTime: yup.string().required("Launch start time is required"),
  };

  // Add conditional fields based on program type
  if (programType?.requiresPayment) {
    Object.assign(baseSchema, {
      isPaymentRequired: yup.string().oneOf(["yes", "no"]),
      currency: yup.string().required("Currency is required"),
      gstPercentage: yup.number(),
    });
  }

  if (programType?.requiresApproval) {
    Object.assign(baseSchema, {
      approvalRequired: yup.string().oneOf(["yes", "no"]).required(),
    });
  }

  if (programType?.maxCapacity > 0) {
    Object.assign(baseSchema, {
      hasSeatLimit: yup.string().oneOf(["yes", "no"]),
      totalSeats: yup.number().max(programType.maxCapacity),
    });
  }

  if (programType?.waitlistApplicable) {
    Object.assign(baseSchema, {
      hasWaitlist: yup.string().oneOf(["yes", "no"]).required(),
      // Fixed the waitlistTriggerCount validation
      waitlistTriggerCount: yup
        .number()
        .when("totalSeats", {
          is: (totalSeats) => totalSeats && totalSeats > 0,
          then: (schema) =>
            schema.max(
              yup.ref("totalSeats"),
              "Waitlist trigger must be less than total seats",
            ),
          otherwise: (schema) => schema,
        })
        .required("Waitlist trigger count is required"),
    });
  }

  if (
    programType?.modeOfOperation === "offline" ||
    programType?.modeOfOperation === "hybrid"
  ) {
    Object.assign(baseSchema, {
      venueAddress: yup
        .array()
        .of(yup.string())
        .min(1, "At least one venue is required"),
    });
  }

  // Add sub-programs schema if hasMultipleSessions is true
  if (programType?.hasMultipleSessions) {
    Object.assign(baseSchema, {
      subPrograms: yup
        .array()
        .of(
          yup.object().shape({
            title: yup.string().required("Session name is required"),
            description: yup.string(),
            modeOfProgram: yup
              .string()
              .oneOf(["online", "offline", "hybrid"])
              .required("Mode of operation is required"),
            // Fixed venue validation
            venueAddress: yup.array().when("modeOfProgram", {
              is: (val: string) => val === "offline" || val === "hybrid",
              then: (schema) => schema.min(1, "At least one venue is required"),
              otherwise: (schema) => schema,
            }),
            banner: yup.string().nullable(),
            bannerImageUrl: yup.string().nullable(),
            seatLimit: yup.string().when("hasSeatLimit", {
              is: "yes",
              then: (schema) => schema.required("Seat limit is required"),
              otherwise: (schema) => schema,
            }),
            registrationStartDate: yup.date(),
            registrationEndDate: yup.date(),
            // Fixed waitlist trigger validation
            waitlistTriggerCount: yup.string().when("hasWaitlist", {
              is: "yes",
              then: (schema) =>
                schema.required("Waitlist trigger count is required"),
              otherwise: (schema) => schema,
            }),

            programStartDate: yup
              .date()
              .required("Program start date is required"),
            programStartTime: yup
              .date()
              .required("Program start time is required"),
            programEndDate: yup
              .date()
              .min(
                yup.ref("programStartDate"),
                "Program end date must be after on program start date",
              )

              .required("Program end date is required"),
            programEndTime: yup.date().required("Program end time is required"),
            checkInStartDate: yup
              .date()
              .required("Check-in start date is required")
              .min(
                yup.ref("programStartDate"),
                "Check-in start date must be after program start date",
              )
              .max(
                yup.ref("programEndDate"),
                "Check-in start date must be before or same as program end date",
              ),
            checkInStartTime: yup
              .date()
              .required("Check-in start time is required"),
            checkInEndDate: yup
              .date()
              .min(
                yup.ref("checkInStartDate"),
                "Check-in end date must be after check-in start date",
              )
              .max(
                yup.ref("programEndDate"),
                "Check-in end date must be before or same as program end date",
              )
              .required("Check-in end date is required"),
            checkInEndTime: yup
              .date()
              .required("Check-in end time is required"),
            checkOutStartDate: yup
              .date()
              .required("Check-out start date is required")
              .min(
                yup.ref("programEndDate"),
                "Check-out start date must be before or same as program end date",
              ),
            checkOutStartTime: yup
              .date()
              .required("Check-out start time is required"),
            checkOutEndDate: yup
              .date()
              .min(
                yup.ref("checkOutStartDate"),
                "Check-out end date must be after check-out start date",
              )
              .min(
                yup.ref("programEndDate"),
                "Check-out end date must be after program end date",
              )
              .required("Check-out end date is required"),
            checkOutEndTime: yup
              .date()
              .required("Check-out end time is required"),
          }),
        )
        .min(1, "At least one session is required"),
    });
  }

  return yup.object().shape(baseSchema);
};

const AddProgramPage = () => {
  // Get programId from URL params instead of location.state
  const { programId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // State variables -- Intial entry point of the program data getting through location state, else it will be null
  const [programType, setProgramType] = useState();
  const [dataOfExitingProgram, setDataOfExitingProgram] = useState();
  const [existingProgramData, setExistingProgramData] = useState();
  const [isEditMode, setIsEditMode] = useState(
    !!existingProgramData || !!location?.state?.isEditMode,
  );
  const [loader, setLoader] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showCustomVenue, setShowCustomVenue] = useState(false);
  const [bannerImageUrl, setBannerImageUrl] = useState<string | null>(
    location?.state?.programType?.data?.bannerUrl || null,
  );
  const [uploadedBanner, setUploadedBanner] = useState<File | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subProgramToDelete, setSubProgramToDelete] = useState<string | null>(
    null,
  );
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [customVenue, setCustomVenue] = useState("");
  const [isCustomVenueModalOpen, setCustomVenueModalOpen] = useState(false);

  // Date range states for main program
  const [dateRange, setDateRange] = useState([null, null]);
  const [registrationDateRange, setRegistrationDateRange] = useState([
    null,
    null,
  ]);

  // Date range states for sub-programs
  const [subProgramDateRanges, setSubProgramDateRanges] = useState<{
    [key: string]: [Date | null, Date | null];
  }>({});

  const [defaultValuesState, setDefaultValuesState] = useState({});

  // Function to create default HDB sessions
  const createDefaultHDBSessions = (programType, startDate, endDate) => {
    const defaultStartTime = parseTimeStringToDate(
      programType?.defaultStartTime,
    );
    const defaultEndTime = parseTimeStringToDate(programType?.defaultEndTime);
    const isResendential = programType?.requiresResidence ? "yes" : "no";
    const venueAddress = programType?.venue ? [programType?.venue] : [];

    const defaultSessions = [
      {
        title: "HDB 1",
        description: "Higher Deeper Beyond Session 1",
        modeOfProgram: programType?.modeOfOperation || "offline",
        hasSeatLimit: programType?.maxCapacity > 0 ? "yes" : "no",
        seatLimit: programType?.maxCapacity?.toString() || "",
        hasWaitlist: programType?.waitlistApplicable ? "yes" : "no",
        waitlistTriggerCount:
          programType?.waitlistTriggerCount?.toString() || "",
        isPaymentRequired: programType?.requiresPayment ? "yes" : "no",
        currency: "INR",
        programFee: programType?.basePrice?.toString() || "",
        isActive: true,
        startTime: defaultStartTime || null,
        endTime: defaultEndTime || null,
        isResendential: isResendential,
        venueAddress: venueAddress,
      },
      {
        title: "HDB 2",
        description: "Higher Deeper Beyond Session 2",
        modeOfProgram: programType?.modeOfOperation || "offline",
        hasSeatLimit: programType?.maxCapacity > 0 ? "yes" : "no",
        seatLimit: programType?.maxCapacity?.toString() || "",
        hasWaitlist: programType?.waitlistApplicable ? "yes" : "no",
        waitlistTriggerCount:
          programType?.waitlistTriggerCount?.toString() || "",
        isPaymentRequired: programType?.requiresPayment ? "yes" : "no",
        currency: "INR",
        programFee: programType?.basePrice?.toString() || "",
        isActive: true,
        startTime: defaultStartTime || null,
        endTime: defaultEndTime || null,
        isResendential: isResendential,
        venueAddress: venueAddress,
      },
      {
        title: "HDB 3",
        description: "Higher Deeper Beyond Session 3",
        modeOfProgram: programType?.modeOfOperation || "offline",
        hasSeatLimit: programType?.maxCapacity > 0 ? "yes" : "no",
        seatLimit: programType?.maxCapacity?.toString() || "",
        hasWaitlist: programType?.waitlistApplicable ? "yes" : "no",
        waitlistTriggerCount:
          programType?.waitlistTriggerCount?.toString() || "",
        isPaymentRequired: programType?.requiresPayment ? "yes" : "no",
        currency: "INR",
        programFee: programType?.basePrice?.toString() || "",
        isActive: true,
        startTime: defaultStartTime || null,
        endTime: defaultEndTime || null,
        isResendential: isResendential,
        venueAddress: venueAddress,
      },
      {
        title: "MSD 1",
        description: "Higher Deeper Beyond Session 4",
        modeOfProgram: programType?.modeOfOperation || "offline",
        hasSeatLimit: programType?.maxCapacity > 0 ? "yes" : "no",
        seatLimit: programType?.maxCapacity?.toString() || "",
        hasWaitlist: programType?.waitlistApplicable ? "yes" : "no",
        waitlistTriggerCount:
          programType?.waitlistTriggerCount?.toString() || "",
        isPaymentRequired: programType?.requiresPayment ? "yes" : "no",
        currency: "INR",
        programFee: programType?.basePrice?.toString() || "",
        isActive: true,
        startTime: defaultStartTime || null,
        endTime: defaultEndTime || null,
        isResendential: isResendential,
        venueAddress: venueAddress,
      },
      {
        title: "MSD 2",
        description: "Higher Deeper Beyond Session 5",
        modeOfProgram: programType?.modeOfOperation || "offline",
        hasSeatLimit: programType?.maxCapacity > 0 ? "yes" : "no",
        seatLimit: programType?.maxCapacity?.toString() || "",
        hasWaitlist: programType?.waitlistApplicable ? "yes" : "no",
        waitlistTriggerCount:
          programType?.waitlistTriggerCount?.toString() || "",
        isPaymentRequired: programType?.requiresPayment ? "yes" : "no",
        currency: "INR",
        programFee: programType?.basePrice?.toString() || "",
        isActive: true,
        startTime: defaultStartTime || null,
        endTime: defaultEndTime || null,
        isResendential: isResendential,
        venueAddress: venueAddress,
      },
    ];

    // Add dates if available
    if (startDate && endDate) {
      const sessionDuration = Math.floor(
        (endDate.getTime() - startDate.getTime()) /
          (1000 * 60 * 60 * 24 * defaultSessions.length),
      );

      defaultSessions.forEach((session, index) => {
        const sessionStartDate = new Date(startDate);
        sessionStartDate.setDate(
          sessionStartDate.getDate() + index * sessionDuration,
        );

        const sessionEndDate = new Date(sessionStartDate);
        sessionEndDate.setDate(
          sessionEndDate.getDate() + Math.max(1, sessionDuration - 1),
        );

        session.startDate = sessionStartDate;
        session.endDate = sessionEndDate;
      });
    }
    return defaultSessions;
  };

  const getExistingSubPrograms = (groupedPrograms: any) => {
    return groupedPrograms.map((session: any) => ({
      id: session.id, // Keep session ID for updates
      title: session.name,
      description: session.description || `${session.name} session`,
      startDate: new Date(session.startDate),
      startsAt: new Date(session?.programStartDate?.toISOString()),
      endDate: new Date(session.endDate),
      modeOfProgram:
        session.modeOfOperation || programType?.modeOfOperation || "offline",
      venueAddress: session.venue ? [session.venue] : [],
      hasSeatLimit: session.totalSeats > 0 ? "yes" : "no",
      seatLimit: session.totalSeats?.toString() || "",
      hasWaitlist: session.waitlistTriggerCount > 0 ? "yes" : "no",
      waitlistTriggerCount: session.waitlistTriggerCount?.toString() || "",
      isPaymentRequired: programType?.requiresPayment ? "yes" : "no",
      currency: programType?.currency || "INR",
      programFee: programType?.basePrice || "",
      isActive: session.isActive !== undefined ? session.isActive : true,
      isResendential: session?.requiresResidence ? "yes" : "no",

      programStartDate: session?.startsAt ? new Date(session.startsAt) : null,
      programStartTime: session?.startsAt ? new Date(session.startsAt) : null,
      programEndDate: session?.endsAt ? new Date(session.endsAt) : null,
      programEndTime: session?.endsAt ? new Date(session.endsAt) : null,
      checkInStartDate: session?.checkinAt ? new Date(session.checkinAt) : null,
      checkInStartTime: session?.checkinAt ? new Date(session.checkinAt) : null,
      checkInEndDate: session?.checkinEndsAt
        ? new Date(session.checkinEndsAt)
        : null,
      checkInEndTime: session?.checkinEndsAt
        ? new Date(session.checkinEndsAt)
        : null,
      checkOutStartDate: session?.checkoutAt
        ? new Date(session.checkoutAt)
        : null,
      checkOutStartTime: session?.checkoutAt
        ? new Date(session.checkoutAt)
        : null,
      checkOutEndDate: session?.checkoutEndsAt
        ? new Date(session.checkoutEndsAt)
        : null,
      checkOutEndTime: session?.checkoutEndsAt
        ? new Date(session.checkoutEndsAt)
        : null,

     }));
  };

  // Updated getDefaultValues function
  const getDefaultValues = () => {
    // Check if program name contains "HDB" to add default sessions
    const programName = programType?.name
      ? `${programType.name} ${new Date().getFullYear()}-${new Date().getFullYear() + 1}`
      : programType?.data?.name
        ? `${programType?.data?.name} ${new Date().getFullYear()}-${new Date().getFullYear() + 1}`
        : "";

    const isHDBProgram = programName.toUpperCase().includes("HDB");


    // Calculate default dates
    const defaultStartDate = programType?.startDate
      ? new Date(programType.startDate)
      : null;
    const defaultEndDate = programType?.endDate
      ? new Date(programType.endDate)
      : null;


    const updatedProgramType = programType?.data;

    // Return default values for new program
    const defaultValues = {
      programName: updatedProgramType?.name,
      description: updatedProgramType?.description || "",
      modeOfProgram: updatedProgramType?.modeOfOperation || "online",
      isPaymentRequired: updatedProgramType?.requiresPayment ? "yes" : "no",
      currency: "INR",
      programFee: updatedProgramType?.basePrice?.toString() || "",
      hdbFee: updatedProgramType?.meta?.price?.[0]?.HDB?.toString() || "",
      msdFee: updatedProgramType.meta?.price?.[1]?.MSD?.toString() || "",
      gstPercentage: updatedProgramType?.gstPercentage?.toString() || "18",
      hasSeatLimit: updatedProgramType?.maxCapacity > 0 ? "yes" : "no",
      seatLimit: updatedProgramType?.maxCapacity?.toString() || "",
      hasWaitlist: updatedProgramType?.waitlistApplicable ? "yes" : "no",
      waitlistTriggerCount:
        updatedProgramType?.waitlistTriggerCount?.toString() || "",
      approvalRequired: updatedProgramType?.requiresApproval ? "yes" : "no",
      startTime: parseTimeStringToDate(updatedProgramType?.defaultStartTime),
      endTime: parseTimeStringToDate(updatedProgramType?.defaultEndTime),
      startDate: updatedProgramType?.startsAt
        ? new Date(updatedProgramType.startsAt)
        : null,
      endDate: updatedProgramType?.endsAt
        ? new Date(updatedProgramType.endsAt)
        : null,
      isResendential: updatedProgramType?.requiresResidence ? "yes" : "no",
      venueAddress: updatedProgramType?.venue ? [updatedProgramType.venue] : [],
      bannerImageUrl: updatedProgramType?.bannerImageUrl || "",
      helpLineNumber: updatedProgramType?.helplineNumber || "",
      emailSenderName: updatedProgramType?.emailSenderName || "",
      venueNameInEmail: updatedProgramType?.venueNameInEmails || "",
      launchStartDate: updatedProgramType?.launchDate
        ? new Date(updatedProgramType.launchDate)
        : null,
      launchStartTime: updatedProgramType?.launchDate
        ? new Date(updatedProgramType.launchDate)
        : null,
      tdsLimit: updatedProgramType?.tdsPercent || "",
      cgstLimit: updatedProgramType?.cgst || "",
      sgstLimit: updatedProgramType?.sgst || "",
      igstLimit: updatedProgramType?.igst || "",
      tdsApplicableTo: updatedProgramType?.tdsApplicability || "",
      nameInInvoice: updatedProgramType?.invoiceSenderName || "",
      pan: updatedProgramType?.invoiceSenderPan || "",
      gstin: updatedProgramType?.gstNumber || "",
      cin: updatedProgramType?.invoiceSenderCin || "",
      address: updatedProgramType?.invoiceSenderAddress || "",
      registrationStartDate: updatedProgramType?.registrationStartsAt
        ? new Date(updatedProgramType.registrationStartsAt)
        : null,
      registrationEndDate: updatedProgramType?.registrationEndsAt
        ? new Date(updatedProgramType.registrationEndsAt)
        : null,
      subPrograms: [], // Will be set below
      registrationStartTime: updatedProgramType?.registrationStartsAt
        ? new Date(updatedProgramType.registrationStartsAt)
        : null,
      registrationEndTime: updatedProgramType?.registrationEndsAt
        ? new Date(updatedProgramType.registrationEndsAt)
        : null,
    };

    // Add default HDB sessions if it's an HDB program and has multiple sessions
    if (
      isHDBProgram &&
      !updatedProgramType?.groupedPrograms &&
      (programType?.hasMultipleSessions ||
        programType.data?.hasMultipleSessions)
    ) {
      defaultValues.subPrograms = createDefaultHDBSessions(
        programType?.data ? programType.data : programType,
        defaultStartDate,
        defaultEndDate,
      );
    } else {
      // If not HDB program, use existing groupedPrograms or empty array
      defaultValues.subPrograms =
        updatedProgramType?.groupedPrograms?.length > 0
          ? getExistingSubPrograms(updatedProgramType?.groupedPrograms)
          : [];
    }
    return defaultValues;
  };


  //Intial Data setup by making an API call to fetch program type details
  const fetchProgramDetails = async (programTypeId: string): Promise<void> => {
    if (!programTypeId) {
      console.error("Program type ID is required");
      return;
    }

    try {
      setLoader(true); // Show loading state
      const response = await getCall(
        !location?.state?.isEditMode
          ? `${endPoints.programType}/${programTypeId}`
          : `${endPoints.program}/${programTypeId}`,
          undefined,
          PORTAL
      );
      if (response?.data) {
        // Set banner image URL if available
        if (response?.data?.data?.bannerUrl) {
          setBannerImageUrl(response.data?.data?.bannerUrl);
        } else {
          setBannerImageUrl(response?.data?.data?.bannerImageUrl);
        }
        // Update state with fetched program type data
        setProgramType(response?.data);
        setIsDataLoaded(true);
      } else {
        console.error("No program type details found");
        alert("Failed to fetch program type details, please try again later.");
        navigate("/admin/choose-program"); // Redirect on error
      }
    } catch (error) {
      console.error("Error fetching program type details:", error);
      alert("Failed to fetch program type details, please try again later.");
      navigate("/admin/choose-program"); // Redirect on error
    } finally {
      setLoader(false); // Hide loading state
    }
  };

  //MOUNTING
  useEffect(() => {
    fetchProgramDetails(location?.state?.programTypeId);
  }, []);

  // Set default values when programType changes
  // This will run when programType is fetched or updated
  // and will reset the form with new default values
  useEffect(() => {
    if (programType?.data) {
      const values = getDefaultValues();
      setDefaultValuesState(values);
      reset(values); // Reset form with default values

      // Explicitly set start and end dates
      if (values.startDate) {
        setValue("startDate", values.startDate);
      }
      if (values.endDate) {
        setValue("endDate", values.endDate);
      }
    }
  }, [programType]);

  // Form setup with dynamic schema and default values
  const {
    control,
    setValue,
    watch,
    getValues,
    trigger,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createProgramSchema(programType?.data || {})),
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUnregister: true,
    defaultValues: defaultValuesState,
  });

  // Watch form values for conditional rendering
  const modeOfProgram = watch("modeOfProgram");
  const isPaymentRequired = watch("isPaymentRequired");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const selectedCurrency = watch("currency");
  const programDescription = watch("description");
  const programName = watch("programName") || "HDB - 25";
  const startTime = watch("startTime");
  const endTime = watch("endTime");

  const seekerDetails = getItemInLocalStorage("seekerDetails");
  const userId = getItemInLocalStorage("seekerDetails")?.id || null;

  const {
    subPrograms,
    subProgramRefs,
    handleAddSubProgram,
    handleSubProgramChange,
    handleSubProgramVenueChange,
    handleDeleteSubProgram: deleteSubProgram,
  } = useSubPrograms({
    programDescription,
    uploadedBanner,
    modeOfProgram,
    selectedCurrency,
    isPaymentRequired,
    existingSubPrograms: existingProgramData
      ? transformResponseDataToFormData(existingProgramData)?.subPrograms
      : [],
  });

  const handleDeleteSubProgram = (subProgramId: string) => {
    setSubProgramToDelete(subProgramId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSubProgram = () => {
    if (subProgramToDelete) {
      deleteSubProgram(subProgramToDelete);
      // Clean up date range state for deleted sub-program
      setSubProgramDateRanges((prev) => {
        const newState = { ...prev };
        delete newState[subProgramToDelete];
        return newState;
      });
      setDeleteDialogOpen(false);
      setSubProgramToDelete(null);
    }
  };
  const handleSave = async (formData: any) => {
    try {
      setLoader(true);
      // Transform subPrograms from form data
      // Usage in handleSave:
      const launchDateTime = getCombinedLaunchDateTime(
        formData.launchStartDate,
        formData.launchStartTime,
      );
      const registrationStartDateTime = getCombinedLaunchDateTime(
        formData.registrationStartDate,
        formData.registrationStartTime,
      );
      const registrationEndDateTime = getCombinedLaunchDateTime(
        formData.registrationEndDate,
        formData.registrationEndTime,
      );

      const programSessions =
        formData?.subPrograms?.map((subProgram, index) => {
          const subProgramStartsAt = getCombinedLaunchDateTime(
            subProgram.programStartDate,
            subProgram.programStartTime,
          );

          const subProgramEndsAt = getCombinedLaunchDateTime(
            subProgram.programEndDate,
            subProgram.programEndTime,
          );
          const subProgramCheckinStartsAt = getCombinedLaunchDateTime(
            subProgram.checkInStartDate,
            subProgram.checkInStartTime,
          );
          const subProgramCheckoutStartsAt = getCombinedLaunchDateTime(
            subProgram.checkOutStartDate,
            subProgram.checkOutStartTime,
          );
          const subProgramCheckinEndsAt = getCombinedLaunchDateTime(
            subProgram.checkInEndDate,
            subProgram.checkInEndTime,
          );
          const subProgramCheckoutEndsAt = getCombinedLaunchDateTime(
            subProgram.checkOutEndDate,
            subProgram.checkOutEndTime,
          );
          return {
            ...(subProgram.id && { id: subProgram.id }), // Include ID if editing existing session
            programId: isEditMode ? existingProgramData.id : programType?.id,
            name: subProgram.title,
            ...(programType?.data?.isGroupedProgram
              ? { groupDisplayOrder: index + 2 }
              : { displayOrder: index + 1 }),

            venue: subProgram.venueAddress?.join(", ") || "",
            modeOfOperation: subProgram.modeOfProgram,
            meetingLink:
              subProgram.modeOfProgram === "online"
                ? "https://zoom.us/j/123456789"
                : undefined,
            meetingId:
              subProgram.modeOfProgram === "online" ? "123456789" : undefined,
            meetingPassword:
              subProgram.modeOfProgram === "online" ? "password123" : undefined,
            totalSeats: 1000,
            bannerImageUrl: subProgram.bannerImageUrl || "",
            waitlistTriggerCount: 0,
            availableSeats: 1000,
            status: "draft",
            isActive: true,
            startsAt: subProgramStartsAt,
            endsAt: subProgramEndsAt,
            checkinAt: subProgramCheckinStartsAt,
            checkoutAt: subProgramCheckoutStartsAt,
            checkinEndsAt: subProgramCheckinEndsAt,
            checkoutEndsAt: subProgramCheckoutEndsAt,
            updatedBy: userId,
            ...(!isEditMode && { createdBy: userId }),
            code: formData?.programName?.includes("HDB")
              ? `HDB${new Date().getFullYear()}0${index}`
              : `ENT${new Date().getFullYear()}0${index}`,
            gstNumber: formData?.gstin,
            invoiceSenderCin: formData?.cin,
            invoiceSenderName: formData?.nameInInvoice || "",
            invoiceSenderPan: formData?.pan,
            isTravelInvolved: true,
            tdsApplicability: formData?.tdsApplicableTo,
            tdsPercent: formData?.tdsLimit,
            sgst: formData.sgstLimit,
            cgst: formData.cgstLimit,
            igst: formData.igstLimit,
            helplineNumber: formData.helpLineNumber,
            emailSenderName: formData.emailSenderName,
            venueNameInEmails: formData.venueNameInEmail,
            currency: formData.currency,
            description: subProgram.description || "",
            hasCheckinCheckout: formData?.programName?.includes("HDB"),
            launchDate: getCombinedLaunchDateTime(
              formData.launchStartDate,
              formData.launchStartTime,
            ),
            invoiceSenderAddress: formData?.address || "",
            basePrice: parseFloat(subProgram?.programFee) || null,
            registrationStartsAt: registrationStartDateTime,
            registrationEndsAt: registrationEndDateTime,
          };
        }) || [];


      const payload = {
        ...(isEditMode && { id: existingProgramData.id }), // Include ID if editing
        typeId: isEditMode
          ? existingProgramData.typeId
          : programType?.data?.id || 1,
        name: formData.programName,
        code: formData?.programName?.includes("HDB")
          ? `HDB${new Date().getFullYear()}`
          : `ENT${new Date().getFullYear()}`,
        description: formData.description,
        modeOfOperation: formData.modeOfProgram,
        onlineType: formData.modeOfProgram === "online" ? "meeting" : undefined,
        startTime: formatTimeToHHMMSS(formData.startTime),
        endTime: formatTimeToHHMMSS(formData.endTime),

        duration: null,
        bannerImageUrl: bannerImageUrl,
        requiresPayment: programType?.data?.requiresPayment || true,
        requiresAttendanceAllSessions:
          programType?.data?.requiresAttendanceAllSessions || false,
        allowsMinors: programType?.data?.allowsMinors || false,
        allowsProxyRegistration:
          programType?.data?.allowsProxyRegistration || false,
        requiresApproval: formData.approvalRequired === "yes",
        totalSeats:
          formData.hasSeatLimit === "yes" ? parseInt(formData.seatLimit) : 0,
        waitlistTriggerCount:
          formData.hasWaitlist === "yes"
            ? parseInt(formData.waitlistTriggerCount)
            : 0,
        availableSeats:
          formData.hasSeatLimit === "yes" ? parseInt(formData.seatLimit) : 0,
        waitlistApplicable: false,
        maxCapacity: formData?.totalSeats || null,
        meta: {
          price: [
            { HDB: parseFloat(formData.hdbFee) || null }, // Add hdbFee
            { MSD: parseFloat(formData.msdFee) || null }, // Add msdFee
          ],
        },
        startsAt: new Date(formData.startDate).toISOString(),
        endsAt: new Date(formData.endDate).toISOString(),
        registrationStartsAt: registrationStartDateTime,
        registrationEndsAt: registrationEndDateTime,
        basePrice: parseFloat(formData.programFee) || null,
        gstPercentage: parseInt(formData.gstPercentage) || 18,
        currency: formData.currency,
        status: isEditMode ? existingProgramData.status : "draft",
        isActive: true,
        noOfSession: 0,
        ...(programType?.data?.isGroupedProgram
          ? {
              groupedPrograms:
                formData?.subPrograms?.length || isEditMode
                  ? programSessions
                  : programSessions,
            }
          : {
              programSessions:
                formData?.subPrograms?.length || isEditMode
                  ? programSessions
                  : programSessions,
            }),
        sgst: formData.sgstLimit,
        cgst: formData.cgstLimit,
        igst: formData.igstLimit,
        helplineNumber: formData.helpLineNumber,
        emailSenderName: formData.emailSenderName,
        venueNameInEmails: formData.venueNameInEmail,
        gstNumber: formData?.gstin,
        invoiceSenderCin: formData?.cin,
        invoiceSenderName: formData?.nameInInvoice || "",
        invoiceSenderPan: formData?.pan,
        isTravelInvolved: true,
        tdsApplicability: formData?.tdsApplicableTo,
        tdsPercent: formData?.tdsLimit,
        venue: formData?.venueAddress?.[0],
        invoiceSenderAddress: formData?.address || "",

        launchDate: launchDateTime,
        updatedBy: userId,
        createdBy: userId,
        ...(isEditMode && { createdBy: userId }),
      };


      let response;

      if (isEditMode) {
        // Make PUT call for editing existing program
        response = await putCall(
          `${endPoints.program}/${existingProgramData.id}`,
          payload,
          PORTAL
        );
      } else {
        // Make POST call for creating new program
        response = await postCall(`${endPoints.program}`, payload, PORTAL);
      }

      if (response.status === 201 || response.status === 200) {
        setLoader(false);
        handleQuestionList(response?.data);
      } else {
        throw new Error(
          `Failed to ${isEditMode ? "update" : "create"} program`,
        );
      }
    } catch (error) {
      setLoader(false);
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} program:`,
        error,
      );
      // Show error message to user
    }
  };

  const handleQuestionList = async (res: unknown) => {
    try {
      const response = await getCall(
        `${endPoints.question}?filters=%7B%22createdBy%22%3A%22-2%22%7D&limit=100`,
        undefined,
        PORTAL
      );
      if (response?.data?.statusCode === 200) {
        const questionsData = response.data.data;

        // Transform the data to match PreviewForm's expected structure
        const transformedQuestions = questionsData?.data?.map(
          (question, index) => ({
            id: index + 1,
            question: {
              id: question.id,
              label: question.label,
              type: question.type,
              config: question.config || {
                isRequired: false,
                minCharacter: null,
                maxCharacters: null,
                validationPattern: null,
                endPoint: null,
              },
              formSection: {
                id: question.formSection?.id || 1, // Default section ID, adjust as needed
                name: question.formSection?.name || "General Information", // Default section name
                description:
                  question.formSection?.description ||
                  "Please fill out the following information",
              },
              questionOptionMaps: question.questionOptionMaps
                ? question.questionOptionMaps.map((optionMap) => ({
                    id: optionMap.id,
                    option: {
                      id: optionMap.option.id,
                      name: optionMap.option.name,
                      type: optionMap.option.type,
                    },
                  }))
                : [],
            },
            displayOrder: index + 1,
          }),
        );

        // Navigate with the transformed data
        navigate("/admin/program-edit", {
          state: {
            programQuestionMaps: transformedQuestions,
            programName: programName,
            bannerImage:
              bannerImageUrl || getProgramImageForBanner(programName),
            programId: res?.data?.id
              ? res?.data?.id
              : res?.data?.primaryProgram?.id,
            fromFormBuilder: false,
          },
        });

        return transformedQuestions;
      }
      throw new Error("Failed to fetch questions");
    } catch (error) {
      console.error("Error fetching questions:", error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate("/admin/choose-program");
  };

  const handleBannerUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (validateFileTypeAndSize(file)) {
      try {
        const imageUrl = await handleAWSFileUpload(file);
        if (imageUrl) {
          // Update both banner and bannerImageUrl fields
          setValue("bannerImageUrl", imageUrl);
          setBannerImageUrl(imageUrl); // update state
          setUploadedBanner(file);
        }
      } catch (error) {
        console.error("File upload failed:", error);
        alert("File upload failed. Please try again.");
      }
    } else {
      return;
    }
  };

  const handleVenueChange = (value: string[]) => {
    if (value.includes("Add Custom Venue")) {
      setShowCustomVenue(true);
      const filteredValue = value.filter((v) => v !== "Add Custom Venue");
      setValue("venueAddress", filteredValue);
    } else {
      setShowCustomVenue(false);
      setValue("venueAddress", value);
    }
  };

  const isDateWithinProgramRange = (date: Date) => {
    if (!startDate || !endDate) return true;
    return date >= startDate && date <= endDate;
  };

  // Main program date handlers
  const handleDateChange = (startDate, endDate) => {
    setValue("startDate", startDate);
    setValue("endDate", endDate);
  };

  const handleRegistrationDateChange = (startDate, endDate) => {
    setValue("registrationStartDate", startDate);
    setValue("registrationEndDate", endDate);
  };

  // Sub-program date range handlers
  const handleSubProgramDateRangeChange = (
    subProgramId: string,
    startDate: Date | null,
    endDate: Date | null,
  ) => {
    setSubProgramDateRanges((prev) => ({
      ...prev,
      [subProgramId]: [startDate, endDate],
    }));

    if (startDate) {
      handleSubProgramChange(subProgramId, "startDate", startDate);
    }
    if (endDate) {
      handleSubProgramChange(subProgramId, "endDate", endDate);
    }
  };

  const getSubProgramDateRange = (
    subProgramId: string,
  ): [Date | null, Date | null] => {
    return subProgramDateRanges[subProgramId] || [null, null];
  };

  const setSubProgramDateRange = (
    subProgramId: string,
    dateRange: [Date | null, Date | null],
  ) => {
    setSubProgramDateRanges((prev) => ({
      ...prev,
      [subProgramId]: dateRange,
    }));
  };


  const handleSubProgramBannerUpload = (
    subProgramId: string,
    imageUrl: string,
  ) => {

    // Update the specific subProgram's banner in form data
    const subProgramIndex = fields.findIndex(
      (field) => field.id === subProgramId,
    );
    if (subProgramIndex !== -1) {
      setValue(
        `subPrograms.${subProgramIndex}.bannerImageUrl` as any,
        imageUrl,
      );
    }
  };

  const handlePreview = () => {
    const currentProgramName = watch("programName") || programName;
    const currentVenue = watch("venueAddress")?.[0] || "";
    const startDate = watch("startDate");
    const endDate = watch("endDate");
    const programFee = watch("programFee") || "0";
    const currency = watch("currency") || "INR";
    setCurrentStep(2);
    navigate("/admin/program-edit", {
      state: {
        programQuestionMaps: existingProgramData?.programQuestionMaps || [],
        programName: currentProgramName,
        bannerImage:
          bannerImageUrl || getProgramImageForBanner(currentProgramName),
        programId: existingProgramData?.id,
        fromFormBuilder: false,
        // Add these fields for RectangularCard
        cardDetails: {
          title: currentProgramName,
          venue: currentVenue,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          payment: {
            amount: programFee,
            currency: currency,
          },
        },
      },
    });
  };

  useEffect(() => {
    if (programType?.data?.venue) {
      setValue("venueAddress", [programType.data.venue]);
    }
  }, [programType?.data?.venue, setValue]);

  return (
    <div>
      {loader && <Loader type="large" />}
      <Box
        component="form"
        onSubmit={handleSubmit(handleSave)}
        sx={{
          background: " #F9FAFB",
        }}
      >
        <div className={styles.headerSection}>
          <div className={styles.programHeader}>
            <div className={styles.programHeaderText}>
              {/* <img
              src={ArrowLeft}
              alt="Back"
              onClick={() => navigate("/admin/action-cards")}
            /> */}
              {isEditMode ? "Update program" : "Create program"}
            </div>
          </div>
          <div>
            <Stepper currentStep={1} />
          </div>

          <div className={styles.headerTitle}>
            Let's begin setting up your {programType?.data?.name} program,{" "}
            {seekerDetails?.firstName || "Seeker"}!
          </div>
        </div>

        {/* Program Details Section */}
        <div className={styles.programDetails}>
          <div className={styles.programDetailsContainer}>
            {/* Render banner image based on state */}
            {bannerImageUrl ? (
              <div
                className={styles.programBanner}
                style={{ position: "relative" }}
              >
                {" "}
                <img
                  src={bannerImageUrl}
                  alt="Program Banner"
                  className={styles.bannerImage}
                />
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="banner-upload-input"
                  onChange={handleBannerUpload}
                />
                <div className={styles.bannerButtonBottomRight}>
                  <Button
                    type="button"
                    buttonClassName={styles.buttonContainer}
                    buttonTextClassName={styles.buttonText}
                    datatestid="add-program-save-button"
                    datatestidText="add-program-save"
                    onClick={() => {
                      const input = document.getElementById(
                        "banner-upload-input",
                      ) as HTMLInputElement;
                      if (input) input.click();
                    }}
                  >
                    change banner
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className={styles.programBanner}
                style={{ position: "relative" }}
              >
                {" "}
                <img
                  src={bannerImageUrl || getProgramImageForBanner(programName)}
                  alt={programName}
                  className={styles.bannerImage}
                />
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="banner-upload-input"
                  onChange={handleBannerUpload}
                />
                <div className={styles.bannerButtonBottomRight}>
                  <Button
                    type="button"
                    buttonClassName={styles.buttonContainer}
                    buttonTextClassName={styles.buttonText}
                    datatestid="add-program-save-button"
                    datatestidText="add-program-save"
                    onClick={() => {
                      const input = document.getElementById(
                        "banner-upload-input",
                      ) as HTMLInputElement;
                      if (input) input.click();
                    }}
                  >
                    change banner
                  </Button>
                </div>
              </div>
            )}
            <div className={styles.programDetailsHeader}>
              <ProgramDetailsForm
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                getValues={getValues}
                trigger={trigger}
                programType={programType}
                uploadedBanner={uploadedBanner}
                bannerImageUrl={bannerImageUrl}
                showCustomVenue={showCustomVenue}
                onBannerUpload={handleBannerUpload}
                onVenueChange={handleVenueChange}
                getCurrencySymbol={getCurrencySymbol}
                currencyOptions={currencyOptions}
                venueOptions={venueOptions}
                dateRange={dateRange}
                setDateRange={setDateRange}
                onDateChange={handleDateChange}
                registrationDateRange={registrationDateRange}
                setRegistrationDateRange={setRegistrationDateRange}
                onRegistrationDateChange={handleRegistrationDateChange}
                isEditMode={isEditMode}
                existingData={existingProgramData}
                onSubProgramBannerUpload={handleSubProgramBannerUpload}
                programTypeNameFromSource={programType?.data?.name || ""}
              />

              <InvoiceDetails control={control} errors={errors} />

              {/* SubProgramsSection: pass bannerImageUrl for each sub-program if needed */}
              {(programType?.data?.noOfSession > 0 ||
                programType?.data?.isGroupedProgram) && (
                <SubProgramsSection
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  errors={errors}
                  getValues={getValues}
                  programType={programType}
                  modeOfProgram={modeOfProgram}
                  startDate={startDate}
                  endDate={endDate}
                  venueOptions={venueOptions}
                  currencyOptions={currencyOptions}
                  isDateWithinProgramRange={isDateWithinProgramRange}
                  onDeleteSubProgram={handleDeleteSubProgram}
                  subProgramDateRanges={subProgramDateRanges}
                  onSubProgramDateRangeChange={handleSubProgramDateRangeChange}
                  getSubProgramDateRange={getSubProgramDateRange}
                  setSubProgramDateRange={setSubProgramDateRange}
                  existingSubPrograms={
                    existingProgramData
                      ? transformResponseDataToFormData(existingProgramData)
                          ?.subPrograms
                      : []
                  }
                  isEditMode={isEditMode}
                  trigger={trigger}
                  noOfSession={programType?.data?.noOfSession || 0}
                  isGroupedProgram={programType?.data?.isGroupedProgram}
                  programTypeNameFromSource={programType?.data?.name || ""}
                />
              )}
            </div>
          </div>
          <RectangularCard
            title={watch("programName")}
            venue={watch("venueAddress")?.[0] || ""}
            startDate={
              watch("startDate") &&
              watch("startDate") instanceof Date &&
              !isNaN(watch("startDate").getTime())
                ? format(watch("startDate"), "yyyy-MM-dd")
                : ""
            }
            endDate={
              watch("endDate") &&
              watch("endDate") instanceof Date &&
              !isNaN(watch("endDate").getTime())
                ? format(watch("endDate"), "yyyy-MM-dd")
                : ""
            }
            payment={{
              amount: watch("programFee") || "0",
              currency: watch("currency") || "INR",
            }}
            hdbFee={watch("hdbFee") || "0"}
            msdFee={watch("msdFee") || "0"}
          />
        </div>
        <div className={styles.formRowButton}>
          <Button
            type="button"
            buttonClassName={styles.buttonContainerSecondary}
            buttonTextClassName={styles.buttonTextSecondary}
            onClick={handleCancel}
            datatestid="add-program-cancel-button"
            datatestidText="add-program-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            buttonClassName={styles.buttonContainer}
            buttonTextClassName={styles.buttonText}
            datatestid="add-program-save-button"
            datatestidText="add-program-save"
          >
            {isEditMode ? "Update Program" : "continue"}
          </Button>
          {existingProgramData?.programQuestionMaps?.length > 0 && (
            <Button
              type="submit"
              buttonClassName={styles.buttonContainer}
              buttonTextClassName={styles.buttonText}
              datatestid="add-program-save-button"
              datatestidText="add-program-save"
              onClick={handlePreview}
            >
              preview form
            </Button>
          )}
        </div>
      </Box>

      <CustomVenueModal
        isOpen={isCustomVenueModalOpen}
        onClose={() => setCustomVenueModalOpen(false)}
        value={customVenue}
        setValue={setCustomVenue}
        title="Adding a Custom Venue"
        message="Enter the Venue Address"
        showInput={true}
      />
    </div>
  );
};

export default AddProgramPage;
